// frontend/src/contexts/WorkspaceContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getCompartilhamento } from '../services/compartilhamentoService';

const WorkspaceContext = createContext(null);

const STORAGE_KEY = 'workspace_titular';

function lidoDoStorage() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export const WorkspaceProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [recebidos, setRecebidos] = useState([]);
  const [concedido, setConcedido] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [carregado, setCarregado] = useState(false);

  const titularAtivoId = lidoDoStorage();

  const carregar = useCallback(async () => {
    if (!isAuthenticated) return;
    setCarregando(true);
    try {
      const data = await getCompartilhamento();
      setRecebidos(data.recebidos || []);
      setConcedido(data.concedido || null);

      // Se o workspace salvo não é mais válido, limpa.
      const salvo = lidoDoStorage();
      if (salvo && !(data.recebidos || []).some((a) => String(a.titular?.id) === String(salvo))) {
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        if (salvo) window.location.assign('/meu-desempenho');
      }
    } catch (e) {
      console.warn('Falha ao carregar compartilhamento', e);
    } finally {
      setCarregando(false);
      setCarregado(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) carregar();
    else {
      setRecebidos([]);
      setConcedido(null);
      setCarregado(false);
    }
  }, [isAuthenticated, user?.id, carregar]);

  // Atualiza ao voltar para a aba (ex.: titular vê "aguardando aceite" virar "ativo").
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) carregar();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAuthenticated, carregar]);

  const trocarWorkspace = useCallback((titularId) => {
    try {
      if (titularId) localStorage.setItem(STORAGE_KEY, String(titularId));
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
    // Reload completo: garante que todo cache/estado da app seja refeito no contexto certo.
    window.location.assign('/meu-desempenho');
  }, []);

  const workspaceAtivo = titularAtivoId
    ? recebidos.find((a) => String(a.titular?.id) === String(titularAtivoId)) || null
    : null;

  // Há um workspace salvo mas os acessos ainda não carregaram: evita que o
  // ProtectedRoute decida cedo demais e bloqueie um convidado sem trial próprio.
  const resolvendoWorkspace = !!titularAtivoId && !carregado;

  return (
    <WorkspaceContext.Provider value={{
      recebidos,
      concedido,
      carregando,
      resolvendoWorkspace,
      workspaceAtivo,            // acesso ativo se estiver operando outro workspace, senão null
      emWorkspaceCompartilhado: !!workspaceAtivo,
      trocarWorkspace,
      recarregarCompartilhamento: carregar,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => useContext(WorkspaceContext);
